import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

import QuickLogDialog from '../components/dashboard/QuickLogDialog';
import CompactKPIBar from '../components/dashboard/CompactKPIBar';
import WelcomeBanner from '../components/dashboard/WelcomeBanner';
import WeeklyAgendaMinus from '../components/dashboard/WeeklyAgendaMinus';
import TodayView from '../components/dashboard/TodayView';
import OpportunitiesInline from '../components/dashboard/OpportunitiesInline';
import UrgentActionsCompact from '../components/dashboard/UrgentActionsCompact';
import RelationshipsCompact from '../components/dashboard/RelationshipsCompact';
import { axiosGetWithRetry } from '../components/dashboard/dashboardHelpers';
import { useUser } from '../context/UserContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const { currentUser } = useUser();
  const [kpis, setKpis] = useState(null);
  const [operational, setOperational] = useState(null);
  const [plan, setPlan] = useState(null);
  const [users, setUsers] = useState([]);
  const [asUserId, setAsUserId] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [planLoading, setPlanLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [welcomeDismissed, setWelcomeDismissed] = useState(() => {
    try {
      return sessionStorage.getItem('dashboard_welcome_dismissed') === '1';
    } catch { return false; }
  });

  const [callDialog, setCallDialog] = useState({ open: false, item: null });
  const [noteDialog, setNoteDialog] = useState({ open: false, item: null });

  const isImpersonating = !!asUserId;
  const readOnly = isImpersonating;

  // Load list of users once (admin only)
  useEffect(() => {
    if ((currentUser?.role || '').toLowerCase() !== 'admin') return;
    (async () => {
      try {
        const token = localStorage.getItem('crm_token');
        const { data } = await axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } });
        setUsers(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to load users', err);
      }
    })();
  }, [currentUser?.role]);

  const fetchAll = useCallback(async () => {
    try {
      setPlanLoading(true);
      setLoadError(null);
      const token = localStorage.getItem('crm_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const baseQ = asUserId ? `as_user_id=${encodeURIComponent(asUserId)}` : '';
      const kpisUrl = baseQ ? `${API}/dashboard/kpis?${baseQ}` : `${API}/dashboard/kpis`;
      const opUrl = baseQ ? `${API}/dashboard/operational?${baseQ}` : `${API}/dashboard/operational`;
      const planParams = new URLSearchParams();
      if (baseQ) planParams.append('as_user_id', asUserId);
      planParams.append('week_offset', String(weekOffset));
      const planUrl = `${API}/dashboard/weekly-plan?${planParams.toString()}`;
      const [kpisRes, opRes, planRes] = await Promise.all([
        axiosGetWithRetry(kpisUrl, { headers }),
        axiosGetWithRetry(opUrl, { headers }),
        axiosGetWithRetry(planUrl, { headers }),
      ]);
      setKpis(kpisRes.data);
      setOperational(opRes.data);
      setPlan(planRes.data);
      // Refetch users list if empty (recovers switcher after transient outage)
      if ((currentUser?.role || '').toLowerCase() === 'admin' && users.length === 0) {
        try {
          const usersRes = await axiosGetWithRetry(`${API}/users`, { headers });
          setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
        } catch (uErr) { console.error('users retry failed', uErr); }
      }
    } catch (err) {
      console.error('Error fetching dashboard', err);
      if (err?.response?.status === 403 && asUserId) {
        toast.error('No tienes permisos para ver ese panel');
        setAsUserId(null);
      } else {
        setLoadError('No se han podido cargar los datos. Reintenta.');
      }
    } finally {
      setLoading(false);
      setPlanLoading(false);
    }
  }, [asUserId, weekOffset, currentUser?.role, users.length]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleToggleNonWorking = async (date, isNonWorking) => {
    try {
      const token = localStorage.getItem('crm_token');
      await axios.post(
        `${API}/dashboard/non-working-days`,
        { date, is_non_working: isNonWorking },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isNonWorking ? 'Día marcado como festivo' : 'Festivo eliminado');
      await fetchAll();
    } catch (err) {
      console.error(err);
      toast.error('Error al actualizar el festivo');
    }
  };

  const handleToggleDone = async (item) => {
    if (readOnly) { toast.error('Modo solo lectura'); return; }
    try {
      const token = localStorage.getItem('crm_token');
      await axios.post(
        `${API}/dashboard/weekly-plan/item/toggle-done`,
        { item_type: item.item_type, item_id: item.item_id, done: !(item.done || item.completed_in_plan) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchAll();
    } catch (err) {
      console.error(err);
      toast.error('Error al marcar');
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="text-sm text-muted-foreground">Cargando panel operativo...</div>
      </div>
    );
  }

  const distribution = plan?.distribution || {};
  const workdays = plan?.workdays || [];
  const todayStr = plan?.today || new Date().toISOString().slice(0, 10);

  return (
    <div className="p-3 md:p-4 space-y-3 md:h-full md:min-h-full md:flex md:flex-col" data-testid="dashboard-page">
      {/* Top bar: compact KPIs + user/timestamp */}
      <CompactKPIBar
        kpis={kpis}
        userName={operational?.user_name}
        generatedAt={operational?.generated_at}
        users={users}
        currentUserRole={currentUser?.role}
        asUserId={asUserId}
        onAsUserChange={setAsUserId}
        isImpersonating={isImpersonating}
        impersonatedName={operational?.as_user_name}
      />

      {loadError && (
        <div
          className="mx-4 mt-2 flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-800"
          data-testid="dashboard-load-error"
        >
          <div className="flex items-center gap-2 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>No se han podido cargar los datos.</span>
          </div>
          <button
            type="button"
            onClick={() => fetchAll()}
            className="text-xs font-medium underline"
            data-testid="dashboard-load-error-retry"
          >
            Reintentar
          </button>
        </div>
      )}

      {!loadError && !welcomeDismissed && !isImpersonating && operational && plan && (
        <WelcomeBanner
          userName={operational?.user_name}
          operational={operational}
          planItems={plan?.distribution?.[plan?.today] || []}
          todayStr={plan?.today || operational?.generated_at?.slice(0, 10)}
          navigate={navigate}
          onDismiss={() => {
            setWelcomeDismissed(true);
            try { sessionStorage.setItem('dashboard_welcome_dismissed', '1'); } catch { /* ignore */ }
          }}
        />
      )}

      {/* Band 1 — Agenda semanal Lun-Vie (full width, hidden on mobile) */}
      <div className="hidden md:block flex-shrink-0" data-testid="band-agenda">
        <WeeklyAgendaMinus
          planDistribution={distribution}
          operationalAgenda={operational?.agenda}
          workdays={workdays}
          todayStr={todayStr}
          navigate={navigate}
          weekOffset={weekOffset}
          onWeekOffsetChange={setWeekOffset}
          nonWorkingDays={plan?.non_working_days || []}
          onToggleNonWorking={readOnly ? undefined : handleToggleNonWorking}
          loading={planLoading}
          weekStart={plan?.week_start}
        />
      </div>

      {/* Band 2 — Hoy (full width, flex-grow on desktop to fill remaining space) */}
      <div className="md:flex-1 md:min-h-0" data-testid="band-today">
        <TodayView
          planDistribution={distribution}
          operationalAgenda={operational?.agenda}
          todayStr={todayStr}
          onQuickCall={(it) => !readOnly && setCallDialog({ open: true, item: it })}
          onQuickNote={(it) => !readOnly && setNoteDialog({ open: true, item: it })}
          onToggleDone={handleToggleDone}
          navigate={navigate}
          readOnly={readOnly}
        />
      </div>

      {/* Band 3 — Urgentes | Oportunidades | A cultivar (3 sub-cards horizontal, fixed height) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:flex-shrink-0" data-testid="band-bottom">
        <UrgentActionsCompact
          actions={operational?.urgent_actions || []}
          count={operational?.urgent_actions_count || 0}
          navigate={navigate}
        />
        <OpportunitiesInline
          opportunities={operational?.attention_opportunities || []}
          navigate={navigate}
        />
        <RelationshipsCompact
          relationships={operational?.relationships || []}
          count={operational?.relationships_count || 0}
          navigate={navigate}
        />
      </div>

      {/* Mini-modals */}
      <QuickLogDialog
        open={callDialog.open}
        onOpenChange={(o) => setCallDialog((s) => ({ ...s, open: o }))}
        kind="call"
        planItem={callDialog.item}
        onSuccess={fetchAll}
      />
      <QuickLogDialog
        open={noteDialog.open}
        onOpenChange={(o) => setNoteDialog((s) => ({ ...s, open: o }))}
        kind="note"
        planItem={noteDialog.item}
        onSuccess={fetchAll}
      />
    </div>
  );
};

export default Dashboard;
